import type {
    ConditionSortField,
    CreatePatientRequest,
    CreatePatientResponse,
    DCDocumentExtractionsResponse,
    DocumentContent,
    DocumentExtractionsResponse,
    DocumentsResponse,
    MedicationSortField,
    PatientCalculationResponse,
    PatientCurationJob,
    PatientEventSortField,
    PatientEventsResponse,
    PatientResponse,
    PatientSearchFilters,
    PatientsResponse,
    PatientVisitsResponse,
    ProcedureSortField,
    ProvenanceDocument,
    ProvenanceRequest,
    SortOrder,
    VisitDetailsResponse,
    VisitSortField,
} from '@packages/types';
import { api } from '@ui/app/api';

export const patientApi = api.injectEndpoints({
    endpoints: builder => ({
        // Get list of patients with optional filters
        getPatientList: builder.query<PatientsResponse, PatientSearchFilters | void>({
            query: args => {
                // Use POST request with body for advanced filtering or when search/order/messageId is provided

                if (
                    args?.filters ||
                    args?.onlyWithDocuments ||
                    args?.documentFilter ||
                    args?.search ||
                    args?.orderBy ||
                    args?.messageId ||
                    args?.clinicalTrialJobId ||
                    args?.randomSampleSize ||
                    args?.randomSeed ||
                    args?.patientIds
                ) {
                    return {
                        url: '/patients/search',
                        method: 'POST',
                        body: {
                            cohortIds: args?.cohortIds,
                            page: args?.page,
                            pageSize: args?.pageSize,
                            filters: args?.filters,
                            includeDocuments: args?.includeDocuments,
                            documentFilter: args?.documentFilter,
                            search: args?.search,
                            orderBy: args?.orderBy,
                            orderDirection: args?.orderDirection,
                            messageId: args?.messageId,
                            clinicalTrialJobId: args?.clinicalTrialJobId,
                            randomSampleSize: args?.randomSampleSize,
                            randomSeed: args?.randomSeed,
                            onlyWithDocuments: args?.onlyWithDocuments,
                            patientIds: args?.patientIds,
                            datasetId: (args as any)?.datasetId,
                            includeElasticsearchForFilters: (args as any)?.includeElasticsearchForFilters,
                        },
                    };
                }

                // Fallback to GET for simple queries
                const params: Record<string, unknown> = {
                    cohortIds: args?.cohortIds?.join(','),
                    page: args?.page,
                    pageSize: args?.pageSize,
                    includeDocuments: args?.includeDocuments,
                };
                if ((args as any)?.includeElasticsearchForFilters) {
                    params.includeElasticsearchForFilters = true;
                }
                return {
                    url: '/patients',
                    params,
                };
            },
            providesTags: ['Patients'],
        }),

        // Get a single patient by ID
        getPatient: builder.query<PatientResponse, string>({
            query: patientId => `/patients/${patientId}`,
            providesTags: (result, error, patientId) => [{ type: 'Patients', id: patientId }],
        }),

        // Get patient counts
        getPatientCounts: builder.query<{ patients: number; records: number }, PatientSearchFilters | void>({
            query: args => {
                return {
                    url: '/patients/counts',
                    method: 'POST',
                    body: args,
                };
            },
            providesTags: ['Patients'],
        }),

        // Get patient events/documents
        getPatientEvents: builder.query<
            PatientEventsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
                search?: string;
                sortBy?: PatientEventSortField;
                sortOrder?: SortOrder;
                excludeObservations?: boolean;
                domainIds?: string[];
                dataSources?: string[];
                chapters?: string[];
            }
        >({
            query: ({
                patientId,
                page = 1,
                pageSize = 20,
                search,
                sortBy,
                sortOrder,
                excludeObservations = true,
                domainIds,
                dataSources,
                chapters,
            }) => ({
                url: `/patients/${patientId}/events`,
                method: 'POST',
                body: {
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    excludeObservations,
                    domainIds,
                    dataSources,
                    chapters,
                },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'PatientEvents', id: patientId }],
            transformResponse: (response: {
                records?: any[];
                total?: number;
                hasNext?: boolean;
                page?: number;
                pageSize?: number;
            }): PatientEventsResponse => ({
                records: response.records || [],
                total: response.total || 0,
                hasNext: response.hasNext || false,
                page: response.page || 1,
                pageSize: response.pageSize || 20,
            }),
        }),

        getPatientCalculations: builder.query<PatientCalculationResponse[], number>({
            query: patientId => `/calculations/patient/${patientId}`,
            providesTags: (result, error, patientId) => [{ type: 'Calculations', id: `patient-${patientId}` }],
        }),

        getPatientProcedures: builder.query<
            PatientEventsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
                search?: string;
                sortBy?: ProcedureSortField;
                sortOrder?: SortOrder;
                startDate?: string;
                endDate?: string;
                dataSources?: string[];
                procedureName?: string;
                sourceValue?: string;
            }
        >({
            query: ({
                patientId,
                page = 1,
                pageSize = 20,
                search,
                sortBy,
                sortOrder,
                startDate,
                endDate,
                dataSources,
                procedureName,
                sourceValue,
            }) => ({
                url: `/patients/${patientId}/procedures`,
                method: 'POST',
                body: {
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    startDate,
                    endDate,
                    dataSources,
                    procedureName,
                    sourceValue,
                },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'PatientEvents', id: patientId }],
            transformResponse: (response: {
                records?: any[];
                total?: number;
                hasNext?: boolean;
                page?: number;
                pageSize?: number;
            }): PatientEventsResponse => ({
                records: response.records || [],
                total: response.total || 0,
                hasNext: response.hasNext || false,
                page: response.page || 1,
                pageSize: response.pageSize || 20,
            }),
        }),

        getPatientMedications: builder.query<
            PatientEventsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
                search?: string;
                sortBy?: MedicationSortField;
                sortOrder?: SortOrder;
                startDate?: string;
                endDate?: string;
                dataSources?: string[];
            }
        >({
            query: ({
                patientId,
                page = 1,
                pageSize = 20,
                search,
                sortBy,
                sortOrder,
                startDate,
                endDate,
                dataSources,
            }) => ({
                url: `/patients/${patientId}/medications`,
                method: 'POST',
                body: { page, pageSize, search, sortBy, sortOrder, startDate, endDate, dataSources },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'PatientEvents', id: patientId }],
            transformResponse: (response: {
                records?: any[];
                total?: number;
                hasNext?: boolean;
                page?: number;
                pageSize?: number;
            }): PatientEventsResponse => ({
                records: response.records || [],
                total: response.total || 0,
                hasNext: response.hasNext || false,
                page: response.page || 1,
                pageSize: response.pageSize || 20,
            }),
        }),

        getPatientConditions: builder.query<
            PatientEventsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
                search?: string;
                sortBy?: ConditionSortField;
                sortOrder?: SortOrder;
                startDate?: string;
                endDate?: string;
                dataSources?: string[];
            }
        >({
            query: ({
                patientId,
                page = 1,
                pageSize = 20,
                search,
                sortBy,
                sortOrder,
                startDate,
                endDate,
                dataSources,
            }) => ({
                url: `/patients/${patientId}/conditions`,
                method: 'POST',
                body: { page, pageSize, search, sortBy, sortOrder, startDate, endDate, dataSources },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'PatientEvents', id: patientId }],
            transformResponse: (response: {
                records?: any[];
                total?: number;
                hasNext?: boolean;
                page?: number;
                pageSize?: number;
            }): PatientEventsResponse => ({
                records: response.records || [],
                total: response.total || 0,
                hasNext: response.hasNext || false,
                page: response.page || 1,
                pageSize: response.pageSize || 20,
            }),
        }),

        getPatientVisits: builder.query<
            PatientVisitsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
                search?: string;
                sortBy?: VisitSortField;
                sortOrder?: SortOrder;
                startDate?: string;
                endDate?: string;
                visitConceptNames?: string[];
                careSiteName?: string;
            }
        >({
            query: ({
                patientId,
                page = 1,
                pageSize = 20,
                search,
                sortBy,
                sortOrder,
                startDate,
                endDate,
                visitConceptNames,
                careSiteName,
            }) => ({
                url: `/patients/${patientId}/visits`,
                method: 'POST',
                body: {
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    startDate,
                    endDate,
                    visitConceptNames,
                    careSiteName,
                },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'PatientEvents', id: patientId }],
            transformResponse: (response: {
                records?: any[];
                total?: number;
                hasNext?: boolean;
                page?: number;
                pageSize?: number;
            }): PatientVisitsResponse => ({
                records: response.records || [],
                total: response.total || 0,
                hasNext: response.hasNext || false,
                page: response.page || 1,
                pageSize: response.pageSize || 20,
            }),
        }),

        getVisitDetails: builder.query<VisitDetailsResponse, { patientId: string; visitId: number }>({
            query: ({ patientId, visitId }) => `/patients/${patientId}/visits/${visitId}/details`,
            providesTags: (result, error, { patientId, visitId }) => [
                { type: 'PatientEvents', id: `visit-${patientId}-${visitId}` },
            ],
        }),

        getPatientDocuments: builder.query<
            DocumentsResponse,
            {
                patientId: string | undefined;
                page?: number;
                pageSize?: number;
            }
        >({
            query: ({ patientId, page = 1, pageSize = 20 }) => ({
                url: `/patients/${patientId}/documents`,
                params: { page, pageSize },
            }),
            providesTags: (result, error, { patientId }) => [{ type: 'Documents', id: `notes-${patientId}` }],
        }),

        getDocumentTypes: builder.query<string[], void>({
            query: () => `/patients/options/document-types`,
            providesTags: ['Documents'],
        }),

        getVisitTypes: builder.query<string[], string | undefined>({
            query: (patientId?: string) => `/patients/options/visit-types?patientId=${patientId || ''}`,
            providesTags: ['PatientEvents'],
        }),

        getDocumentContent: builder.query<DocumentContent, { patientId: string; noteId: number }>({
            query: ({ patientId, noteId }) => `/patients/${patientId}/documents/${noteId}`,
            providesTags: (result, error, { patientId, noteId }) => [
                { type: 'Documents', id: `content-${patientId}-${noteId}` },
            ],
        }),

        getDocumentExtractions: builder.query<DocumentExtractionsResponse, { patientId: string; noteId: number }>({
            query: ({ patientId, noteId }) => `/patients/${patientId}/documents/${noteId}/extractions`,
            providesTags: (result, error, { patientId, noteId }) => [
                { type: 'Documents', id: `extractions-${patientId}-${noteId}` },
            ],
        }),

        // Get completed curation jobs for a patient
        getPatientCurationJobs: builder.query<PatientCurationJob[], string>({
            query: patientId => `/patients/${patientId}/curation-jobs`,
            providesTags: (result, error, patientId) => [{ type: 'Documents', id: `curation-jobs-${patientId}` }],
        }),

        // Get DC document extractions for a specific document within a curation job
        getDCDocumentExtractions: builder.query<
            DCDocumentExtractionsResponse,
            { patientId: string; noteId: number; jobId: string }
        >({
            query: ({ patientId, noteId, jobId }) => ({
                url: `/patients/${patientId}/documents/${noteId}/dc-extractions`,
                params: { jobId },
            }),
            providesTags: (result, error, { patientId, noteId, jobId }) => [
                { type: 'Documents', id: `dc-extractions-${patientId}-${noteId}-${jobId}` },
            ],
        }),

        // Create a new patient
        createPatient: builder.mutation<CreatePatientResponse, CreatePatientRequest>({
            query: body => ({
                url: '/patients',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Patients'],
        }),

        // Get provenance source document for a record
        getProvenance: builder.mutation<ProvenanceDocument, ProvenanceRequest>({
            query: body => ({
                url: '/provenance/source_document',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useGetPatientListQuery,
    useLazyGetPatientListQuery,
    useGetPatientQuery,
    useGetPatientCountsQuery,
    useGetPatientEventsQuery,
    useGetPatientProceduresQuery,
    useGetPatientMedicationsQuery,
    useGetPatientConditionsQuery,
    useGetPatientVisitsQuery,
    useGetVisitDetailsQuery,
    useGetPatientDocumentsQuery,
    useGetPatientCalculationsQuery,
    useGetDocumentTypesQuery,
    useGetVisitTypesQuery,
    useGetDocumentContentQuery,
    useGetDocumentExtractionsQuery,
    useGetPatientCurationJobsQuery,
    useGetDCDocumentExtractionsQuery,
    useGetProvenanceMutation,
    useCreatePatientMutation,
} = patientApi;
